import { Component, Inject, OnInit, AfterViewChecked, ChangeDetectorRef} from '@angular/core';
import { MODAL_DATA, NgbActiveModal } from '@agilent/ui-element';
import { AC, ACApiService, acNamePattern, acNameLengthPattern, acNamePatternNotOnlyNumbers } from '@app/api/ac-api.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import Utils from '@app/utils/utils';
import { AgMacAddressPipe } from '@app/shared/pipes/ag-mac-address.pipe';
import { CidDeviceApiService } from '@app/api/cid-device-api.service';
import { ApiError } from '@app/api/api.service';
import { CustomValidators } from '@app/shared/custom-input-validators';
import { Router } from '@angular/router';
import { OpenLabSystem, OpenLabSystemApiService } from '@app/api/openlab-system-api.service';
import { Customer } from '@app/api/customer-api.service';


enum ErrorMessage {
    NOT_FOUND = 'PIN code not found',
    IN_USE = 'PIN code is in use',
    INVALID_PIN_LENGTH = 'PIN code must be 8 characters long',
    INVALID_PIN_PATTERN = 'PIN code must be letters and numbers only',
    INVALID_CID_NAME_PATTERN = 'Only letters, numbers, and hyphens are allowed',
    INVALID_CID_NAME_PATTERN_NOT_ONLY_NUMBERS ='Name containing only numbers is not allowed',
    INVALID_CID_NAME_LENGTH = 'Name must be at least 3 characters and at most 15 characters long',
    CID_NAME_NOT_UNIQUE = 'Name is already in use for this customer',
    INVALID_CID_FQDN = 'Please enter a valid FQDN',
}

export const pinLengthPattern: RegExp = /^.{8}$/;
export const pinPattern: RegExp = /^([a-zA-Z0-9]+)*$/;

@Component({
    selector: 'app-add-cid',
    styleUrls: [ './add-cid-dialog.component.css' ],
    templateUrl: './add-cid-dialog.component.html'
})

export class AddCidDialogComponent implements OnInit, AfterViewChecked  {
    isAddCid = true;
    addUpdateBtnLabel = 'Add';
    dialogTitle = 'Add CID';
    ac: AC;
    acToEdit: AC = {
        ac_dashboard_name: '',
        certificate_arn: '',
        customer_id: 0,
        id: 0,
        identity: '',
        is_deleted: false,
        name: '',
        thing_name: '',
        modified_date: null,
        mac_address: '',
        register_ip_address: '',
        register_fqdn: '',
        register_token_name: '',
        online_status: '',
        platform: '',
        location: '',
        description: '',
        openlab_system_id: 0,
        fqdn: '',
        is_locked: false,
        last_ok_sw_update: '',
        cid_status: '',
        pin_code: '',
        update_status: ''
    };
    isLoading: boolean;
    acForm: FormGroup;
    updateACErrorValue: string;
    isPinDisabled = false;
    private pinCodeErrorMsg: string;
    private pinCode: string;
    private macAddress = '';
    private isPinLoading: boolean;
    private cidNameErrorMsg: string;
    private createCidErrorValue: string;
    private isCidCreated: boolean;
    private isCidSaving: boolean;
    private formValues: any;
    private customer: Customer;
    private isSuperuser: boolean;
    private openLabSystems: OpenLabSystem[] = [];
    private selectedOpenLabSystem: OpenLabSystem = null;
    private newCid: AC;

    constructor(
        private activeModal: NgbActiveModal,
        @Inject(MODAL_DATA) private modalData: any,
        private openLabSystemApiService: OpenLabSystemApiService,
        private cidDeviceApiService: CidDeviceApiService,
        private acApiService: ACApiService,
        private router: Router,
        private cdRef: ChangeDetectorRef
    ) {
        this.customer = this.modalData.customer;
        this.isSuperuser = this.modalData.isSuperuser;
        this.pinCode = this.modalData.pinCode;
        // Check if we have an AC to edit
        this.ac = this.modalData.existingAC || {};
        if (modalData.existingAC) {
            // Make a copy of the original AC
            this.acToEdit = { ...this.modalData.existingAC };
            this.isAddCid = false;
            this.addUpdateBtnLabel = 'Update';
            this.dialogTitle = 'Edit CID ';
            this.pinCode = this.acToEdit.pin_code;
            this.macAddress = this.acToEdit.mac_address;
        }
        this.ac.pin_code = this.pinCode;
    }

    ngAfterViewChecked() {
        this.cdRef.detectChanges();
    }

    async ngOnInit() {

        this.isLoading = true;

        if (this.acToEdit.pin_code) {
            this.isPinDisabled = true;
        }
        this.acForm = new FormGroup({
            pinCode: new FormControl({ value: this.ac.pin_code, disabled: this.isPinDisabled },
                [
                    CustomValidators.blankField('PIN code'),
                    CustomValidators.pattern(pinLengthPattern, ErrorMessage.INVALID_PIN_LENGTH),
                    CustomValidators.pattern(pinPattern, ErrorMessage.INVALID_PIN_PATTERN)
                ]),
            macAddress: new FormControl({ value: new AgMacAddressPipe().transform(this.macAddress, 'separator'), disabled: true }),
            openlab_system_id: new FormControl(this.ac.openlab_system_id),
            name: new FormControl({ value: this.ac.name, disabled: !this.isAddCid },
                [
                    CustomValidators.blankField('Name'),
                    CustomValidators.pattern(acNameLengthPattern, ErrorMessage.INVALID_CID_NAME_LENGTH),
                    CustomValidators.pattern(acNamePattern, ErrorMessage.INVALID_CID_NAME_PATTERN),
                    CustomValidators.pattern(acNamePatternNotOnlyNumbers, ErrorMessage.INVALID_CID_NAME_PATTERN_NOT_ONLY_NUMBERS),
                    CustomValidators.trailinghypen()
                ]),
            location: new FormControl(this.ac.location),
            description: new FormControl(this.ac.description),
            fqdn: new FormControl({ value: this.ac.fqdn , disabled: false},
                [
                    CustomValidators.blankField('FQDN'),
                    CustomValidators.pattern(Utils.SERVER_FQDN_PATTERN, ErrorMessage.INVALID_CID_FQDN)
                ]),
            user_reason: new FormControl()
        });

        await this.load();
    }

    async load() {
        try {
            if (this.isAddCid) {
                const olsPromise = await this.openLabSystemApiService.getOpenLabSystems({
                    limit: 1000,
                    offset: 0,
                    customerId: this.customer.id
                });

                // Get OpenLab systems
                const olsResp = await olsPromise;
                this.openLabSystems = olsResp.openlab_systems;
                for (const ols of this.openLabSystems) {
                    if (ols.id === this.customer.default_openlab_system_id) {
                        this.selectedOpenLabSystem = ols;
                        break;
                    }
                }
                if (this.pinCode) {
                    this.pinCode = this.pinCode.toUpperCase();
                    await this.fetchCidDevice(this.pinCode);
                }
            }

            this.acForm.get('pinCode').valueChanges.subscribe(async pin => {
                this.acForm.get('pinCode').setValue(pin.toUpperCase(), { emitEvent: false });
                this.pinCode = this.acForm.get('pinCode').value;
                this.ac.pin_code = this.pinCode;
                if (pin && pin.trim().length === 8 && !this.acForm.get('pinCode').invalid) {
                    this.resetFormServerErrorMessages();
                    this.pinCode = this.acForm.get('pinCode').value;
                    await this.fetchCidDevice(this.acForm.get('pinCode').value);
                }
                else  {
                    this.resetFormServerErrorMessages();
                    this.acForm.controls['macAddress'].reset();
                }

            });
            this.acForm.get('name').valueChanges.subscribe(_ => this.cidNameErrorMsg = null);

            this.isLoading = false;
        }
        catch (ex) {
            console.error('Error loading data', ex);
        }
    }

    async fetchCidDevice(pinCode) {
        this.isPinLoading = true;
        try {
            const resp = await this.cidDeviceApiService.getCidDevice({
                pinCode: pinCode
            });
            this.macAddress = resp.cid_device.mac_address;
            this.macAddress = new AgMacAddressPipe().transform(this.macAddress, 'separator');
            this.acForm.controls['macAddress'].setValue(this.macAddress);
        }
        catch (apiError) {
            this.handleServerError(apiError);
        }
        finally {
            this.isPinLoading = false;
        }
    }

    private handleServerError(apiError: ApiError) {
        // Make sure error received has the expected fields
        if (!apiError.data || !apiError.data.error) {
            // In case of an unexpected server error - fallback on "NOT FOUND"
            // Server error happens when a wierd pin code is provided that
            // bypasses front-end checks but does not match the variable rule in the url.
            this.setFormServerError('pinCode', ErrorMessage.NOT_FOUND);
            return;
        }

        if (apiError.data.error === 'in_use') {
            this.macAddress = apiError.data.cid_device.mac_address;
            this.macAddress = new AgMacAddressPipe().transform(this.macAddress, 'separator');
            this.acForm.controls['macAddress'].setValue(this.macAddress);
            this.setFormServerError('pinCode', ErrorMessage.IN_USE);
        }
        else {
            // Failing to fetch a device for the pin code should result in NOT FOUND
            this.setFormServerError('pinCode', ErrorMessage.NOT_FOUND);
        }

    }

    private setFormServerError(fieldName: string, errorMessage: string) {
        if (fieldName === 'pinCode') {
            this.pinCodeErrorMsg = errorMessage;
        }
        if (fieldName === 'name') {
            this.cidNameErrorMsg = errorMessage;
        }
    }

    private resetFormServerErrorMessages() {
        this.pinCodeErrorMsg = null;
    }

    async createCid() {
        try {
            this.createCidErrorValue = null;
            this.isCidCreated = false;

            const acResp = await this.acApiService.createCid({
                customerId: this.customer.id,
                name: this.ac.name.trim(),
                fqdn: this.ac.fqdn,
                openLabSystemId: this.ac.openlab_system_id['id'],
                location: this.ac.location,
                description: this.ac.description,
                pinCode: this.ac.pin_code
            });
            this.newCid = acResp.ac;
            this.pinCode = acResp.pin_code;
            this.isCidCreated = true;
        }
        catch (ex) {
            console.error('Error creating CID', ex);
            this.createCidErrorValue = ex.data && ex.data.error;
            if (this.createCidErrorValue === 'ac_name_must_be_unique_per_customer') {
                this.setFormServerError('name', ErrorMessage.CID_NAME_NOT_UNIQUE);
            }
        }
        finally {
            this.isLoading = false;
        }
    }

    /**
     * Saves the updated values of the AC
     */
    async save() {
        this.isLoading = true;
        this.isCidSaving = true;
        try {
            Object.assign(this.ac, this.acForm.value);
            if (this.modalData.existingAC) {

                if (this.isPinDisabled) {
                    this.formValues = this.acForm.value;
                }
                else {
                    this.formValues = this.acForm.getRawValue();
                }
                const acUpdate = { ...this.acToEdit, ...this.formValues };
                // Remove these fields before submitting the update.
                // The server does not expect them.
                delete acUpdate.shadow_json;
                delete acUpdate.online_status;

                // Remove all whitespace
                Utils.trimObjectValues(acUpdate);

                const updateResp = await this.acApiService.updateAC({
                    ac: acUpdate
                });
                const newAC = updateResp.ac;

                // Pass the value returned from the server back to the UI
                this.activeModal.close({ ac: newAC, message: 'Update successful' });
            }
            else {
                await this.createCid();
                if (!this.isCidCreated) {
                    return;
                }
                this.finish();
                return;
            }
        }
        catch (ex) {
            this.updateACErrorValue = ex.data && ex.data.error;
            this.activeModal.dismiss(ex.data.error);
        }
        finally {
            this.isCidSaving = false;
        }
    }

    cancel() {
        this.activeModal.close({});
        if (this.isAddCid) {
            this.router.navigate(
                ['/cids']
            );
        }
    }

    finish() {
        this.activeModal.close({ success: true, cid: this.newCid });
    }

    formIsReady(reasonForm: FormGroup) {
        this.acForm.setControl('user_reason', reasonForm.get('userReason'));
    }

    isButtonEnabled() {
        return this.acForm.valid && !this.pinCodeErrorMsg && !this.isPinLoading;
    }
}
